import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { CheckCircle2, Edit3, GitBranch, GripVertical, Plus, Save, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Breadcrumbs, EmptyState, PageHeader, Table } from '~/components/data';
import { EntityAuditPanel, ItemPicker } from '~/components/domain';
import { useToast } from '~/components/feedback';
import { Button, Checkbox, Dialog, Drawer, Input, Select } from '~/components/primitives';
import {
  activateBom,
  addBomItem,
  cloneBom,
  deleteBom,
  fetchBomUsage,
  fetchBomVersionsByTemplate,
  removeBomItem,
  updateBom,
  updateBomItem,
} from '~/lib/api/boms';
import { ApiRequestError } from '~/lib/api/client';
import { type BomItemRow, explodeBom, fetchBom } from '~/lib/api/inventory';
import { validateAuditHighlight } from '~/lib/audit/highlight-search';
import shared from './_auth._shared.module.css';
import styles from './_auth.boms.$bomId.module.css';

export const Route = createFileRoute('/_auth/boms/$bomId')({
  // F105 (С-I): `?highlight=<audit_event_id>` — запись журнала, которую открыть в панели истории.
  validateSearch: validateAuditHighlight,
  staticData: { minMainWidth: 960 },
  component: BomDetail,
});

function uomOptions(): { value: string; label: string }[] {
  return [
    { value: 'pcs', label: m.route_bom_detail_uom_pcs() },
    { value: 'kg', label: m.route_bom_detail_uom_kg() },
    { value: 'g', label: m.route_bom_detail_uom_g() },
    { value: 'm', label: m.route_bom_detail_uom_m() },
    { value: 'm2', label: m.route_bom_detail_uom_m2() },
    { value: 'l', label: m.route_bom_detail_uom_l() },
    { value: 'set', label: m.route_bom_detail_uom_set() },
  ];
}

function BomDetail(): JSX.Element {
  const { bomId } = useParams({ from: '/_auth/boms/$bomId' });
  const { highlight: auditHighlight } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({ queryKey: ['boms', bomId], queryFn: () => fetchBom(bomId) });
  const [qty, setQty] = useState('1');
  const [explosion, setExplosion] = useState<unknown[] | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [approverId, setApproverId] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteUsage, setDeleteUsage] = useState<{
    loading: boolean;
    can_delete?: boolean;
    work_order_count?: number;
  }>({ loading: false });

  const [editingHeader, setEditingHeader] = useState(false);
  const [editName, setEditName] = useState('');
  const [editOutputQty, setEditOutputQty] = useState('');
  const [editUom, setEditUom] = useState('');
  const [editIsActive, setEditIsActive] = useState(false);

  const [addItemOpen, setAddItemOpen] = useState(false);
  const [newItemId, setNewItemId] = useState('');
  const [newItemQty, setNewItemQty] = useState('1');
  const [newItemUom, setNewItemUom] = useState('pcs');
  const [newItemScrap, setNewItemScrap] = useState('0');
  const [newItemAlternate, setNewItemAlternate] = useState(false);

  const header = data?.header;
  const items = data?.items ?? [];

  const templateKey = useMemo(() => {
    const key = header?.bomTemplateKey ?? header?.bom_template_key ?? header?.code ?? '';
    return key.replace(/-v\d+$/, '') || key;
  }, [header]);

  const historyQuery = useQuery({
    queryKey: ['boms', 'history', templateKey],
    queryFn: () => fetchBomVersionsByTemplate(templateKey),
    enabled: historyOpen && Boolean(templateKey),
  });

  const explodeMut = useMutation({
    mutationFn: () => explodeBom(bomId, qty),
    onSuccess: (res) => setExplosion(res.requirements),
  });

  const cloneMut = useMutation({
    mutationFn: () => cloneBom(bomId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['boms'] });
    },
  });

  const activateMut = useMutation({
    mutationFn: () => activateBom(bomId, approverId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['boms'] });
      setActivateDialogOpen(false);
      setActivateError(null);
    },
    onError: (err) => {
      if (err instanceof ApiRequestError && err.status === 409) {
        setActivateError(m.route_bom_detail_activate_err_conflict());
      } else {
        setActivateError(
          err instanceof Error ? err.message : m.route_bom_detail_activate_err_generic(),
        );
      }
    },
  });

  const updateItemMut = useMutation({
    mutationFn: (p: {
      bomItemId: string;
      qty?: string;
      scrap_pct?: number;
      is_alternate?: boolean;
    }) =>
      updateBomItem(bomId, p.bomItemId, {
        ...(p.qty !== undefined ? { qty: p.qty } : {}),
        ...(p.scrap_pct !== undefined ? { scrap_pct: p.scrap_pct } : {}),
        ...(p.is_alternate !== undefined ? { is_alternate: p.is_alternate } : {}),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['boms', bomId] }),
  });

  const updateHeaderMut = useMutation({
    mutationFn: () =>
      updateBom(bomId, {
        name: editName,
        output_qty: editOutputQty,
        uom: editUom,
        is_active: editIsActive,
      }),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.route_bom_detail_toast_header_saved() });
      setEditingHeader(false);
      void qc.invalidateQueries({ queryKey: ['boms', bomId] });
      void qc.invalidateQueries({ queryKey: ['boms'] });
    },
    onError: (err: Error) =>
      toast.push({
        variant: 'error',
        title: m.route_bom_detail_toast_save_failed(),
        description: err.message,
      }),
  });

  const addItemMut = useMutation({
    mutationFn: () =>
      addBomItem(bomId, {
        item_id: newItemId,
        qty: newItemQty,
        uom: newItemUom,
        position: items.length,
        scrap_pct: newItemScrap || undefined,
        is_alternate: newItemAlternate,
      }),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.route_bom_detail_toast_material_added() });
      setAddItemOpen(false);
      setNewItemId('');
      setNewItemQty('1');
      setNewItemUom('pcs');
      setNewItemScrap('0');
      setNewItemAlternate(false);
      void qc.invalidateQueries({ queryKey: ['boms', bomId] });
    },
    onError: (err: Error) =>
      toast.push({
        variant: 'error',
        title: m.route_bom_detail_toast_material_add_failed(),
        description: err.message,
      }),
  });

  const removeItemMut = useMutation({
    mutationFn: (itemId: string) => removeBomItem(bomId, itemId),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.route_bom_detail_toast_material_removed() });
      void qc.invalidateQueries({ queryKey: ['boms', bomId] });
    },
    onError: (err: Error) =>
      toast.push({
        variant: 'error',
        title: m.route_bom_detail_toast_material_remove_failed(),
        description: err.message,
      }),
  });

  const deleteBomMut = useMutation({
    mutationFn: () => deleteBom(bomId),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.route_bom_detail_toast_deleted() });
      void qc.invalidateQueries({ queryKey: ['boms'] });
      void navigate({ to: '/boms' });
    },
    onError: (err) => {
      if (err instanceof ApiRequestError && err.status === 409) {
        toast.push({
          variant: 'error',
          title: m.route_bom_detail_toast_delete_used(),
          description: m.route_bom_detail_toast_delete_used_desc(),
        });
      } else {
        toast.push({
          variant: 'error',
          title: m.route_bom_detail_toast_delete_failed(),
          description:
            err instanceof Error ? err.message : m.route_bom_detail_toast_delete_failed_generic(),
        });
      }
      setDeleteDialogOpen(false);
    },
  });

  const openDeleteDialog = async (): Promise<void> => {
    setDeleteDialogOpen(true);
    setDeleteUsage({ loading: true });
    try {
      const usage = await fetchBomUsage(bomId);
      setDeleteUsage({ loading: false, ...usage });
    } catch {
      setDeleteUsage({ loading: false, can_delete: false });
    }
  };

  const startEditHeader = (): void => {
    setEditName(header?.name ?? '');
    setEditOutputQty(header?.outputQty ?? '1');
    setEditUom(header?.uom ?? 'pcs');
    setEditIsActive(header?.isActive ?? header?.is_active ?? false);
    setEditingHeader(true);
  };

  const bomIsActive = header?.isActive ?? header?.is_active ?? false;
  const bomCode =
    header?.code ?? header?.bomTemplateKey ?? header?.bom_template_key ?? bomId.slice(0, 8);

  return (
    <div className={shared.page}>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: m.nav_boms(), to: '/boms' },
              { label: header ? `${bomCode} v${header.version}` : '…' },
            ]}
          />
        }
        title={
          header
            ? m.route_bom_detail_title_with_code({ code: bomCode, version: String(header.version) })
            : m.route_bom_detail_title_fallback()
        }
        description={
          header
            ? bomIsActive
              ? m.route_bom_detail_desc_active()
              : m.route_bom_detail_desc_inactive()
            : undefined
        }
        actions={
          <>
            {!editingHeader && (
              <Button
                variant="ghost"
                size="sm"
                leadingIcon={<Edit3 size={14} />}
                onClick={startEditHeader}
              >
                {m.route_bom_detail_btn_edit()}
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<GitBranch size={14} />}
              onClick={() => setHistoryOpen(true)}
            >
              {m.route_bom_detail_btn_history()}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => cloneMut.mutate()}
              disabled={cloneMut.isPending}
            >
              {cloneMut.isPending
                ? m.route_bom_detail_btn_clone_pending()
                : m.route_bom_detail_btn_clone()}
            </Button>
            {!bomIsActive && (
              <Button
                variant="primary"
                size="sm"
                leadingIcon={<CheckCircle2 size={14} />}
                onClick={() => setActivateDialogOpen(true)}
              >
                {m.route_bom_detail_btn_activate()}
              </Button>
            )}
            <Button
              variant="destructive"
              size="sm"
              leadingIcon={<Trash2 size={14} />}
              onClick={() => void openDeleteDialog()}
            >
              {m.route_bom_detail_btn_delete()}
            </Button>
          </>
        }
      />

      {header && !editingHeader && (
        <section className={styles.headerCard}>
          <HeaderField
            label={m.route_bom_detail_field_code()}
            value={header.bomTemplateKey ?? header.bom_template_key ?? '—'}
          />
          <HeaderField label={m.route_bom_detail_field_name()} value={header.name ?? '—'} />
          <HeaderField
            label={m.route_bom_detail_field_fg()}
            value={
              header.item
                ? m.route_bom_detail_fg_label_dual({ name: header.item.name, sku: header.item.sku })
                : (header.itemId ?? header.item_id ?? '').slice(0, 8)
            }
          />
          <HeaderField
            label={m.route_bom_detail_field_output_qty()}
            value={header.outputQty ?? '—'}
            mono
          />
          <HeaderField label={m.route_bom_detail_field_uom()} value={header.uom ?? '—'} />
          <HeaderField
            label={m.route_bom_detail_field_version()}
            value={m.route_bom_detail_version_prefix({ version: String(header.version) })}
            mono
          />
          <HeaderField
            label={m.route_bom_detail_field_active()}
            value={bomIsActive ? m.route_bom_detail_yes() : m.route_bom_detail_no()}
          />
        </section>
      )}

      {header && editingHeader && (
        <section className={styles.editCard}>
          <h3 className={shared.sectionHeading}>{m.route_bom_detail_edit_heading()}</h3>
          <div className={shared.formGrid3}>
            <Input
              label={m.route_bom_detail_edit_code()}
              value={header.bomTemplateKey ?? header.bom_template_key ?? '—'}
              disabled
              hint={m.route_bom_detail_edit_not_editable()}
            />
            <Input
              label={m.route_bom_detail_edit_fg()}
              value={
                header.item
                  ? m.route_bom_detail_fg_label_dual({
                      name: header.item.name,
                      sku: header.item.sku,
                    })
                  : (header.itemId ?? header.item_id ?? '').slice(0, 8)
              }
              disabled
              hint={m.route_bom_detail_edit_not_editable()}
            />
            <Input
              label={m.route_bom_detail_edit_name()}
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />
            <Input
              label={m.route_bom_detail_edit_output_qty()}
              value={editOutputQty}
              onChange={(e) => setEditOutputQty(e.target.value)}
              inputMode="decimal"
              className={shared.mono}
            />
            <Select
              label={m.route_bom_detail_edit_uom()}
              value={editUom}
              onChange={(e) => setEditUom(e.target.value)}
              options={uomOptions()}
            />
            <Checkbox
              label={m.route_bom_detail_edit_active()}
              checked={editIsActive}
              onChange={(e) => setEditIsActive(e.target.checked)}
            />
          </div>
          <div className={styles.actionsRow}>
            <Button
              variant="primary"
              size="sm"
              leadingIcon={<Save size={14} />}
              onClick={() => updateHeaderMut.mutate()}
              disabled={updateHeaderMut.isPending || !editName.trim() || !editOutputQty.trim()}
              loading={updateHeaderMut.isPending}
            >
              {m.route_bom_detail_btn_save()}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<X size={14} />}
              onClick={() => setEditingHeader(false)}
            >
              {m.route_bom_detail_btn_cancel()}
            </Button>
          </div>
        </section>
      )}

      <section className={styles.materialsSection}>
        <div className={styles.materialsHeader}>
          <h3 className={shared.sectionHeading}>{m.route_bom_detail_materials_heading()}</h3>
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<Plus size={14} />}
            onClick={() => setAddItemOpen(true)}
          >
            {m.route_bom_detail_btn_add_material()}
          </Button>
        </div>
        {items.length > 0 ? (
          <ul className={shared.resetList}>
            {items.map((item) => (
              <BomItemRowComp
                key={item.id}
                item={item}
                onEdit={(patch) => updateItemMut.mutate({ bomItemId: item.id, ...patch })}
                onRemove={() => removeItemMut.mutate(item.id)}
                pending={updateItemMut.isPending}
                removePending={removeItemMut.isPending}
              />
            ))}
          </ul>
        ) : (
          <EmptyState
            title={m.route_bom_detail_empty_title()}
            description={m.route_bom_detail_empty_desc()}
          />
        )}
      </section>

      <section className={styles.explodeSection}>
        <h3 className={shared.sectionHeading}>{m.route_bom_detail_explode_heading()}</h3>
        <div className={styles.explodeRow}>
          <Input
            label={m.route_bom_detail_explode_qty_label()}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            inputMode="decimal"
            className={shared.mono}
          />
          <Button onClick={() => explodeMut.mutate()} disabled={explodeMut.isPending}>
            {m.route_bom_detail_btn_explode()}
          </Button>
        </div>
        {explosion && (
          <Table
            ariaLabel={m.route_bom_detail_explode_aria()}
            data={explosion as Array<Record<string, unknown>>}
            rowKey={(r, i) => String(r.item_id ?? i)}
            columns={[
              {
                id: 'item',
                header: m.route_bom_detail_col_item(),
                accessor: (r) => String(r.item_id ?? ''),
                mono: true,
              },
              {
                id: 'qty',
                header: m.route_bom_detail_col_qty(),
                accessor: (r) => String(r.qty ?? ''),
                mono: true,
                align: 'right',
              },
              {
                id: 'uom',
                header: m.route_bom_detail_col_uom(),
                accessor: (r) => String(r.uom ?? ''),
                mono: true,
              },
              {
                id: 'depth',
                header: m.route_bom_detail_col_depth(),
                accessor: (r) => String(r.depth ?? ''),
                mono: true,
                align: 'right',
              },
            ]}
          />
        )}
      </section>

      <Dialog
        open={addItemOpen}
        onClose={() => setAddItemOpen(false)}
        title={m.route_bom_detail_add_item_title()}
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setAddItemOpen(false)}>
              {m.route_bom_detail_btn_cancel()}
            </Button>
            <Button
              variant="primary"
              onClick={() => addItemMut.mutate()}
              disabled={
                addItemMut.isPending ||
                !newItemId ||
                !newItemQty ||
                !/^-?\d+(\.\d+)?$/.test(newItemQty)
              }
              loading={addItemMut.isPending}
            >
              {m.route_bom_detail_add_item_confirm()}
            </Button>
          </>
        }
      >
        <div className={styles.dialogBody}>
          <ItemPicker
            label={m.route_bom_detail_add_item_picker_label()}
            value={newItemId}
            onChange={setNewItemId}
          />
          <Input
            label={m.route_bom_detail_add_item_qty_label()}
            value={newItemQty}
            onChange={(e) => setNewItemQty(e.target.value)}
            inputMode="decimal"
            className={shared.mono}
          />
          <Select
            label={m.route_bom_detail_add_item_uom_label()}
            value={newItemUom}
            onChange={(e) => setNewItemUom(e.target.value)}
            options={uomOptions()}
          />
          <Input
            label={m.route_bom_detail_add_item_scrap_label()}
            value={newItemScrap}
            onChange={(e) => setNewItemScrap(e.target.value)}
            inputMode="decimal"
            className={shared.mono}
          />
          <Checkbox
            label={m.route_bom_detail_add_item_alt_label()}
            checked={newItemAlternate}
            onChange={(e) => setNewItemAlternate(e.target.checked)}
          />
        </div>
      </Dialog>

      <Drawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={m.route_bom_detail_history_title()}
        width={520}
      >
        {historyQuery.isLoading ? (
          <p className={styles.helpText}>{m.route_bom_detail_history_loading()}</p>
        ) : (
          <Table
            ariaLabel={m.route_bom_detail_history_aria()}
            data={historyQuery.data?.items ?? []}
            rowKey={(v) => v.id}
            columns={[
              {
                id: 'v',
                header: m.route_bom_detail_col_version(),
                accessor: (v) => m.route_bom_detail_version_prefix({ version: String(v.version) }),
                mono: true,
              },
              {
                id: 'active',
                header: m.route_bom_detail_col_active(),
                // FE-47 sweep: pill вместо bare ✓ char (Calm UI §4.4).
                accessor: (v) => (
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 500,
                      background: v.is_active ? 'var(--ok-bg-15)' : 'var(--bg-surface)',
                      color: v.is_active ? 'var(--color-ok)' : 'var(--color-text-muted)',
                      border: `1px solid ${v.is_active ? 'var(--color-ok)' : 'var(--border)'}`,
                    }}
                  >
                    {v.is_active ? '✓ Активна' : '— Неактивна'}
                  </span>
                ),
              },
              {
                id: 'wo',
                header: m.route_bom_detail_col_open_wo(),
                accessor: (v) => v.open_work_orders_count,
                mono: true,
                align: 'right',
              },
              {
                id: 'created',
                header: m.route_bom_detail_col_created_at(),
                accessor: (v) => new Date(v.created_at).toLocaleDateString(),
                mono: true,
              },
              {
                id: 'by',
                header: m.route_bom_detail_col_created_by(),
                accessor: (v) => v.created_by ?? '—',
              },
            ]}
          />
        )}
      </Drawer>

      <Dialog
        open={activateDialogOpen}
        onClose={() => setActivateDialogOpen(false)}
        title={m.route_bom_detail_activate_title()}
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setActivateDialogOpen(false)}>
              {m.route_bom_detail_btn_cancel()}
            </Button>
            <Button
              variant="primary"
              onClick={() => activateMut.mutate()}
              disabled={activateMut.isPending || !approverId}
              loading={activateMut.isPending}
            >
              {m.route_bom_detail_activate_confirm()}
            </Button>
          </>
        }
      >
        <div className={styles.dialogBody}>
          <p className={styles.helpText}>{m.route_bom_detail_activate_hint()}</p>
          <Input
            label={m.route_bom_detail_approver_label()}
            value={approverId}
            onChange={(e) => setApproverId(e.target.value)}
            placeholder={m.route_bom_detail_approver_placeholder()}
            className={shared.mono}
          />
          {activateError && <p className={styles.warningText}>{activateError}</p>}
        </div>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={m.route_bom_detail_delete_title()}
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteDialogOpen(false)}>
              {m.route_bom_detail_btn_cancel()}
            </Button>
            <Button
              variant="destructive"
              disabled={deleteUsage.loading || !deleteUsage.can_delete || deleteBomMut.isPending}
              loading={deleteBomMut.isPending}
              onClick={() => deleteBomMut.mutate()}
            >
              {m.route_bom_detail_btn_delete()}
            </Button>
          </>
        }
      >
        {deleteUsage.loading ? (
          <p className={styles.helpText}>{m.route_bom_detail_delete_usage_loading()}</p>
        ) : deleteUsage.can_delete ? (
          <p>
            {m.route_bom_detail_delete_confirm_prefix()}
            <strong>{bomCode}</strong>
            {m.route_bom_detail_delete_confirm_suffix()}
          </p>
        ) : (deleteUsage.work_order_count ?? 0) > 0 ? (
          <p className={styles.warningText}>
            {m.route_bom_detail_delete_blocked_wo({ count: deleteUsage.work_order_count ?? 0 })}
          </p>
        ) : (
          <p className={styles.warningText}>{m.route_bom_detail_delete_blocked_generic()}</p>
        )}
      </Dialog>

      <EntityAuditPanel entity_type="bom" entity_id={bomId} highlight={auditHighlight} />
    </div>
  );
}

function HeaderField({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className={styles.headerField}>
      <div className={styles.headerFieldLabel}>{label}</div>
      <div className={[styles.headerFieldValue, mono ? shared.mono : ''].filter(Boolean).join(' ')}>
        {value}
      </div>
    </div>
  );
}

function BomItemRowComp({
  item,
  onEdit,
  onRemove,
  pending,
  removePending,
}: {
  item: BomItemRow;
  onEdit: (patch: { qty?: string; scrap_pct?: number; is_alternate?: boolean }) => void;
  onRemove: () => void;
  pending: boolean;
  removePending: boolean;
}): JSX.Element {
  const [localQty, setLocalQty] = useState(item.qty);
  const [localScrap, setLocalScrap] = useState(item.scrapPct ?? '0');

  return (
    <li className={styles.materialRow}>
      <GripVertical size={14} aria-hidden="true" className={styles.grip} />
      <span className={styles.itemIdCell} title={item.itemId}>
        {item.item
          ? m.route_bom_detail_item_label_dual({ name: item.item.name, sku: item.item.sku })
          : item.itemId.slice(0, 8)}
      </span>
      <div className={styles.inlineField}>
        <span className={styles.inlineFieldLabel}>{m.route_bom_detail_inline_qty_label()}</span>
        <input
          type="text"
          value={localQty}
          onChange={(e) => setLocalQty(e.target.value)}
          onBlur={() => {
            if (localQty !== item.qty) onEdit({ qty: localQty });
          }}
          className={styles.numInput}
          disabled={pending}
        />
      </div>
      <span className={styles.uomLabel}>{item.uom}</span>
      <div className={styles.inlineField}>
        <span className={styles.inlineFieldLabel}>{m.route_bom_detail_inline_scrap_label()}</span>
        <input
          type="number"
          min="0"
          max="100"
          step="0.1"
          value={localScrap}
          onChange={(e) => setLocalScrap(e.target.value)}
          onBlur={() => {
            const v = Number.parseFloat(localScrap);
            if (!Number.isNaN(v) && String(v) !== (item.scrapPct ?? '0')) onEdit({ scrap_pct: v });
          }}
          className={styles.numInput}
          disabled={pending}
        />
      </div>
      <Checkbox
        label={m.route_bom_detail_alt_short()}
        checked={item.isAlternate ?? false}
        onChange={(e) => onEdit({ is_alternate: e.target.checked })}
        disabled={pending}
      />
      <Button
        size="sm"
        variant="ghost"
        leadingIcon={<Trash2 size={12} />}
        onClick={onRemove}
        disabled={removePending}
        aria-label={m.route_bom_detail_aria_remove()}
      >
        {''}
      </Button>
    </li>
  );
}
